import { pulseContactRoleEnum } from '@markku/db'
import { isIsoWeekStart } from '@markku/utils'
import { z } from 'zod'

export type PulseContactRole = (typeof pulseContactRoleEnum.enumValues)[number]

// Five statements about the past week on a 1–5 agree scale, each something
// the worker saw happen (or not) and each pointing at one lever management
// can pull.
export const PULSE_ITEMS = [
  { key: 'time', label: 'Ehdin tehdä päivän työt työpäivän aikana.' },
  { key: 'help', label: 'Saan apua, jos tarvitsen sitä.' },
  {
    key: 'tools',
    label: 'Minulla oli kohteissa tarvittavat tiedot ja työvälineet.',
  },
  {
    key: 'safety',
    label: 'Pystyin tekemään työt turvallisesti.',
  },
  { key: 'voice', label: 'Uskallan sanoa, jos jokin ei toimi.' },
] as const

export type PulseItemKey = (typeof PULSE_ITEMS)[number]['key']

export const PULSE_CONTACT_ROLE_LABELS: Record<PulseContactRole, string> = {
  supervisor: 'Esihenkilö',
  safety_rep: 'Työsuojeluvaltuutettu',
  hr: 'HR-johtaja',
  ceo: 'Toimitusjohtaja',
}

// Weeks are keyed by their Monday; any other date would form a bucket of its
// own that no weekly grid shows.
const weekStartSchema = z
  .string()
  .refine(isIsoWeekStart, 'weekStart must be a Monday (YYYY-MM-DD)')

// Sent whole every time: the first step carries only the score, the second
// (and any later edit) the full state. The row is replaced by what arrives.
// No contactRole withdraws an open request.
export const submitPulseSchema = z.object({
  weekStart: weekStartSchema,
  score: z.number().int().min(1).max(5),
  comment: z.string().trim().max(2000).optional(),
  answers: z.record(z.string(), z.number().int().min(1).max(5)).default({}),
  contactRole: z.enum(pulseContactRoleEnum.enumValues).optional(),
})

export const pulseStatusInputSchema = z.object({ weekStart: weekStartSchema })

// The worker's own answer for the week, so the app can show it as answered
// and prefill an edit; `response` is null until they answer. The whole status
// is null for users the pulse is not for (no employee row), so the app shows
// them nothing.
const pulseStatusSchema = z.object({
  response: z
    .object({
      score: z.number(),
      comment: z.string().nullable(),
      answers: z.record(z.string(), z.number()),
    })
    .nullable(),
  contactRole: z.enum(pulseContactRoleEnum.enumValues).nullable(),
})
export const pulseStatusResponseSchema = pulseStatusSchema.nullable()

export const pulseSummaryInputSchema = z.object({
  // The viewer's current week, so the grid is in their timezone.
  weekStart: weekStartSchema,
  // At least the trailing four weeks, which lastFourWeeks pools.
  weeks: z.number().int().min(4).max(52).default(12),
})

const pulseCellSchema = z.object({
  weekStart: z.string(),
  count: z.number(),
  avgScore: z.number().nullable(),
})

export const pulseSummaryResponseSchema = z.object({
  headcount: z.number(),
  weeks: z.array(pulseCellSchema),
  // All answers of the last four weeks pooled; weekStart is the window start.
  lastFourWeeks: pulseCellSchema,
  groups: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      headcount: z.number(),
      weeks: z.array(pulseCellSchema),
    }),
  ),
  items: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      readings: z.array(pulseCellSchema),
    }),
  ),
  // No row id: with one id sequence across tables it would pair a comment
  // with the named contact request written in the same request.
  comments: z.array(
    z.object({
      weekStart: z.string(),
      score: z.number(),
      comment: z.string(),
      groupLabel: z.string(),
    }),
  ),
  // Named by the worker's own choice; management forwards each to the person
  // asked for and marks it handled.
  contactRequests: z.array(
    z.object({
      id: z.number(),
      weekStart: z.string(),
      contactRole: z.enum(pulseContactRoleEnum.enumValues),
      handledAt: z.string().nullable(),
      user: z.object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string().nullable(),
        phoneNumber: z.string().nullable(),
      }),
    }),
  ),
})

export const markPulseContactHandledSchema = z.object({ id: z.number() })

export type SubmitPulse = z.infer<typeof submitPulseSchema>
export type PulseStatusResponse = z.infer<typeof pulseStatusSchema>
export type PulseSummaryInput = z.infer<typeof pulseSummaryInputSchema>
export type PulseSummaryResponse = z.infer<typeof pulseSummaryResponseSchema>
export type PulseCell = z.infer<typeof pulseCellSchema>
